import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { PencilSimple, DownloadSimple, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

/*
  InvoiceQuickViewDialog · Iter 85.66
  ──────────────────────────────────
  Client-facing invoice preview inside the Invoices workspace. Renders the
  invoice EXACTLY as the recipient will see it (matches the visual language
  of `PublicInvoiceView.jsx`) — invoice number, from/bill-to, dates, period,
  full itemized table with hours/rate/amount, subtotal + tax + total, work
  summary, notes, payment terms.

  Closing the modal returns the advisor to the same Invoices workspace with
  no navigation. Edit / Download / Send actions surface here too so a
  partner can complete the review-and-send loop without leaving the modal.
*/
const STATUS_TONE = {
  paid: { bg: "#0f766e", label: "Paid" },
  sent: { bg: "#B08048", label: "Sent" },
  draft: { bg: "#7A7F8C", label: "Draft" },
  overdue: { bg: "#B23A48", label: "Overdue" },
  void: { bg: "#4a4a4a", label: "Void" },
};

const fmt$ = (n, ccy = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "USD" }).format(Number(n || 0));
const shortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return iso; }
};

export default function InvoiceQuickViewDialog({
  invoice, open, onOpenChange,
  onEdit, onSend, onDownload, onDelete,
}) {
  const [firm, setFirm] = useState({});
  // Load firm settings once for letterhead so the preview matches what
  // gets emailed / downloaded / shown at the public view.
  useEffect(() => {
    if (!open) return;
    api.get("/firm/settings")
      .then((r) => setFirm(r.data || {}))
      .catch(() => setFirm({}));
  }, [open]);

  if (!invoice) return null;
  const ccy = invoice.currency || firm.currency || "USD";
  const status = invoice.status || "draft";
  const tone = STATUS_TONE[status] || STATUS_TONE.draft;
  const isDraft = status === "draft";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-[#f5f5f4] max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0 border-0"
        data-testid="invoice-quickview-dialog"
      >
        {/* Sticky action bar — keeps advisor on the Invoices workspace on close. */}
        <DialogHeader className="sticky top-0 z-10 bg-card border-b border-border px-5 py-3 flex-row items-center justify-between gap-3">
          <DialogTitle className="font-display text-sm flex items-center gap-2">
            Invoice preview
            <span
              className="inline-block text-[0.5625rem] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 text-white"
              style={{ background: tone.bg }}
              data-testid="invoice-quickview-status"
            >
              {tone.label}
            </span>
          </DialogTitle>
          <div className="flex items-center gap-2">
            {isDraft && onEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(invoice)}
                data-testid="invoice-quickview-edit"
                className="rounded-none border-border"
              >
                <PencilSimple size={12} weight="bold" className="mr-1" /> Edit
              </Button>
            )}
            {onDownload && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(invoice)}
                data-testid="invoice-quickview-download"
                className="rounded-none border-border"
              >
                <DownloadSimple size={12} weight="bold" className="mr-1" /> Download
              </Button>
            )}
            {onSend && isDraft && (
              <Button
                size="sm"
                onClick={() => onSend(invoice)}
                data-testid="invoice-quickview-send"
                className="rounded-none bg-primary text-primary-foreground"
              >
                <PaperPlaneTilt size={12} weight="fill" className="mr-1" /> Send
              </Button>
            )}
            {onDelete && isDraft && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDelete(invoice)}
                data-testid="invoice-quickview-delete"
                className="rounded-none border-destructive/60 text-destructive hover:bg-destructive/10"
              >
                <Trash size={12} weight="bold" className="mr-1" /> Delete
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Client-facing sheet — mirrors PublicInvoiceView.jsx letterhead. */}
        <div className="p-6">
          <div className="bg-white border border-[#e5e5e5] shadow-sm">
            {/* Header */}
            <div className="px-8 py-6 border-b-2 border-[#B08048] flex items-start justify-between">
              <div>
                <div className="text-xl font-bold tracking-tight text-[#1B1E28]">{firm.name || "Advisory Firm"}</div>
                {firm.tagline && (
                  <div className="text-[0.6875rem] uppercase tracking-[0.14em] text-[#7A7F8C] mt-1">{firm.tagline}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-[#B08048]">Invoice</div>
                <div className="font-mono text-lg text-[#1B1E28] mt-1" data-testid="invoice-quickview-number">
                  {invoice.invoice_number}
                </div>
              </div>
            </div>

            {/* Meta grid */}
            <div className="px-8 py-5 grid grid-cols-3 gap-5 text-sm border-b border-[#e5e5e5]">
              <div>
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-1">From</div>
                <div className="font-medium text-[#1B1E28]">{firm.name || "—"}</div>
                {firm.address && <div className="text-[#7A7F8C] text-xs whitespace-pre-line mt-0.5">{firm.address}</div>}
                {firm.email && <div className="text-[#7A7F8C] text-xs">{firm.email}</div>}
                {firm.phone && <div className="text-[#7A7F8C] text-xs">{firm.phone}</div>}
                {firm.website && <div className="text-[#7A7F8C] text-xs">{firm.website}</div>}
              </div>
              <div>
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-1">Bill to</div>
                <div className="font-medium text-[#1B1E28]" data-testid="invoice-quickview-client">{invoice.client_name}</div>
                {invoice.engagement_name && (
                  <div className="text-[#7A7F8C] text-xs mt-0.5" data-testid="invoice-quickview-engagement">
                    {invoice.engagement_name}
                  </div>
                )}
              </div>
              <div className="text-xs">
                <div className="flex justify-between py-0.5">
                  <span className="text-[#7A7F8C]">Issue date</span>
                  <span className="font-mono text-[#1B1E28]">{shortDate(invoice.issue_date)}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[#7A7F8C]">Due date</span>
                  <span className="font-mono text-[#1B1E28]">{shortDate(invoice.due_date)}</span>
                </div>
                <div className="flex justify-between py-0.5" data-testid="invoice-quickview-period">
                  <span className="text-[#7A7F8C]">Period</span>
                  <span className="font-mono text-[#1B1E28]">
                    {shortDate(invoice.period_start)} → {shortDate(invoice.period_end)}
                  </span>
                </div>
              </div>
            </div>

            {/* Line items */}
            <div className="px-8 py-5">
              <table className="w-full text-sm" data-testid="invoice-quickview-items">
                <thead>
                  <tr className="bg-[#1B1E28] text-white text-[0.625rem] font-mono uppercase tracking-[0.18em]">
                    <th className="text-left px-3 py-2 font-normal">Description</th>
                    <th className="text-right px-3 py-2 font-normal">Hours</th>
                    <th className="text-right px-3 py-2 font-normal">Rate</th>
                    <th className="text-right px-3 py-2 font-normal">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.line_items || []).map((li, i) => (
                    <tr key={i} className="border-b border-[#f0f0f0]">
                      <td className="px-3 py-2.5 text-[#1B1E28]">{li.description || li.title}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-[#1B1E28]">
                        {Number(li.hours ?? li.quantity ?? 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[#1B1E28]">{fmt$(li.rate, ccy)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-[#1B1E28]">{fmt$(li.amount, ccy)}</td>
                    </tr>
                  ))}
                  {(invoice.line_items || []).length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-3 py-6 text-center text-xs text-[#7A7F8C]">
                        No line items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="mt-4 ml-auto max-w-xs text-sm" data-testid="invoice-quickview-totals">
                <div className="flex justify-between py-1">
                  <span className="text-[#7A7F8C]">Subtotal</span>
                  <span className="font-mono text-[#1B1E28]">{fmt$(invoice.subtotal, ccy)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-[#7A7F8C]">Tax ({firm.tax_rate ?? 0}%)</span>
                  <span className="font-mono text-[#1B1E28]">{fmt$(invoice.tax_amount, ccy)}</span>
                </div>
                <div className="flex justify-between py-2 mt-1 border-t-2 border-[#1B1E28]">
                  <span className="text-[#1B1E28] font-semibold">Total due</span>
                  <span className="font-mono text-[#B08048] font-bold text-lg" data-testid="invoice-quickview-total">
                    {fmt$(invoice.total, ccy)}
                  </span>
                </div>
              </div>
            </div>

            {invoice.ai_summary && (
              <div className="px-8 pb-4">
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-2">Work summary</div>
                <p className="text-sm text-[#1B1E28] leading-relaxed whitespace-pre-line">{invoice.ai_summary}</p>
              </div>
            )}
            {invoice.notes && (
              <div className="px-8 pb-4">
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-2">Notes</div>
                <p className="text-sm text-[#1B1E28] leading-relaxed whitespace-pre-line" data-testid="invoice-quickview-notes">
                  {invoice.notes}
                </p>
              </div>
            )}
            {firm.invoice_footer && (
              <div className="px-8 py-4 border-t border-[#e5e5e5] text-xs text-[#7A7F8C] whitespace-pre-line">
                {firm.invoice_footer}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
